import PlaylistTrack, { type IProgressPlaylist } from "./PlaylistTrack";

import { audioTypeFromPath, cachedBlob } from "./PlaylistCache";

import type { IPlaylistDB } from "../../interfaces/Playlist";

import PlaylistManager from "../Managers/PlaylistManager";

import PlaylistEventsManager from "./PlaylistEventsManager";

import { openDatabase } from "../../Utils/db";

import { PlaylistEvents } from "./PlaylistEventsManager.interface";

import BucketCacheService from "../../infrastructure/Services/BucketCacheService";

import FilesService from "../../infrastructure/Services/FilesService";

export type IWatch = (item: IProgressPlaylist) => void;

/**
 * Resultado de un intento de reproducción, visto desde `Playlist`:
 *
 * - `started`: la pista PEDIDA está sonando.
 * - `loaded`: la pista pedida es la actual y está cargada, pero no suena —el
 *   arranque lo interrumpió un `pause()`—. No prende el transporte, pero SÍ es
 *   la pista vigente: la UI tiene que mostrarla.
 * - `skipped`: no es la actual (rota, sin URL, o pisada por otro `play()`).
 */
type PlayOutcome = "started" | "loaded" | "skipped";

class Playlist {
  /**
   * `null` = no hay pista instalada. Era un campo con `!` (definite
   * assignment), así que TypeScript daba por sentado que SIEMPRE había control
   * y no marcaba el `seek()` sin guarda que tiraba un TypeError al arrastrar
   * la barra de progreso sin haber tocado Play nunca.
   */
  private PlaylistControl: PlaylistTrack | null = null;

  /**
   * Ya se anunció `Started` por el control instalado. Una pista que quedó
   * CARGADA Y EN PAUSA (el usuario pausó mientras se bajaba) nunca emitió
   * `Started`, así que el disparador `playlist_started` se saltaba esa pista
   * entera: lo emite el `resume()` que finalmente la pone a sonar.
   */
  private startedAnnounced = false;

  private AudioContext!: AudioContext;

  private MediaStreamDestination!: MediaStreamAudioDestinationNode;

  private currentTrackIndex = 0;

  private isLoopEnabled = true;

  /**
   * Último volumen pedido por el usuario. `PlaylistTrack.getVolume()` no sirve
   * como memoria: devuelve `null` cuando la pista anterior nunca llegó a crear
   * su <audio> (p.ej. una fuente remota que no se pudo bajar).
   */
  private currentVolume = 1;

  /**
   * Pistas seguidas que no se pudieron reproducir. Corta la cadena
   * play → skip → nextTrack → play cuando NINGUNA de la lista se puede
   * reproducir; sin esto sería recursión infinita.
   */
  private unplayableStreak = 0;

  private Data: Map<number, IPlaylistDB> = new Map();

  constructor(
    audioContext: AudioContext,

    destination: MediaStreamAudioDestinationNode,
  ) {
    this.AudioContext = audioContext;

    this.MediaStreamDestination = destination;
  }

  async set(playlist: IPlaylistDB[]) {
    // Clear existing data to ensure deleted tracks are removed

    this.Data.clear();

    // La racha es contra el tamaño de la lista, así que tiene que empezar de
    // cero con cada lista nueva: los intentos fallidos de la anterior se
    // contaban contra ESTA y podían cortar la rotación con `PlaylistEmpty`
    // antes de probar todas las pistas recién cargadas.

    this.unplayableStreak = 0;

    for (const item of playlist) {
      this.Data.set(item.id, item);

      PlaylistManager.set(item.id, item);
    }

    const filesService = new FilesService("api");

    const workspacePrefix = "eriol-workspace://";

    const tasks = playlist.map(async (item) => {
      const url = PlaylistManager.get(item.id);

      if (!url) return;

      // El MISMO lector que usa `PlaylistTrack`: una entrada de 0 bytes no es
      // una copia —reproducirla es un <audio> mudo que nunca dispara `ended`—
      // y un storage que TIRA (particionado, modo privado, permisos) no puede
      // cancelar el precacheo. Acá esa excepción se la tragaba el
      // `allSettled` de abajo y la lista entera se quedaba sin precachear en
      // silencio.

      const cached = await cachedBlob(url);

      if (cached) return;

      if (url.startsWith(workspacePrefix)) {
        const path = url.slice(workspacePrefix.length);

        const parts = path.split("/");

        if (parts.length >= 5) {
          const dir = `/${parts[0]}/${parts[1]}/${parts[2]}/${parts[3]}/`;

          const file = parts.slice(4).join("/");

          let blob = await filesService.readFile(dir, file);

          if (!blob || blob.size === 0) {
            for (let i = 0; i < 3; i++) {
              await new Promise((r) => setTimeout(r, 300));

              blob = await filesService.readFile(dir, file);

              if (blob && blob.size > 0) break;
            }
          }

          if (blob && blob.size > 0) {
            await BucketCacheService.put(url, blob, audioTypeFromPath(file));
          }
        }
      }
    });

    await Promise.allSettled(tasks);
  }

  /**
   * Devuelve `true` solo si la pista PEDIDA arrancó. `false` significa que no
   * sonó: o la lista está vacía, o hubo que saltear — y en ese caso el
   * `nextTrack()` de adentro del salteo ya avisó por `ChangeTrack` cuál es la
   * que realmente suena. Sin esta señal `nextTrack()` emitía `ChangeTrack` por
   * la pista fallada igual, incluso después de cerrar la rotación con
   * `PlaylistEmpty`/`Ended`, y la UI volvía a seleccionarla.
   */
  async play(trackNum = 0): Promise<boolean> {
    return (await this.playTrack(trackNum)) === "started";
  }

  /**
   * El intento de reproducción con su resultado COMPLETO. `play()` se queda
   * solo con "¿suena?" —que es de lo que sale el transporte de la UI— pero la
   * rotación necesita distinguir "no sonó porque el usuario pausó" (la pista
   * ES la actual: hay que anunciarla) de "no sonó porque se salteó" (la actual
   * es otra, y el salteo ya la anunció).
   */
  private async playTrack(trackNum: number): Promise<PlayOutcome> {
    this.currentTrackIndex = trackNum;

    const playlist = Array.from(this.Data.values());

    if (playlist.length === 0) return "skipped";

    const track = playlist[trackNum];

    if (!track) return "skipped";

    const url = PlaylistManager.get(track.id);

    // Sin URL la pista no es reproducible (una fila local sin artista ni álbum

    // no llega al Dictionary). Antes esto era un `return` seco y la rotación

    // automática se clavaba en ese índice: `onTrackEnded` nunca se registraba y

    // `nextTrack()` solo se llama desde ese callback, así que la emisión se

    // quedaba en aire muerto. Ahora se salta a la siguiente.

    if (!url) {
      // El salteo puede terminar sin instalar ningún control —si ésta es la
      // última fila (o la única) irreproducible se cierra la rotación con
      // `PlaylistEmpty`/`Ended`—, y entonces el <audio> anterior se quedaba
      // sonando: la UI detenida y la emisión al aire con música.
      //
      // Pero pararlo ANTES de saltear es silencio en emisión durante TODA la
      // cadena de salteo, que puede tardar lo que tarde en fallar la pista
      // siguiente (el tope de la bajada remota, o los reintentos de lectura
      // del workspace). El camino normal ya para el <audio> viejo en el
      // momento justo —`stopCurrentTrack()` corre recién cuando hay una pista
      // nueva que instalar—, así que acá alcanza con cubrir el caso que ese
      // camino no llega a cubrir: que el salteo NO haya instalado nada.

      const previous = this.PlaylistControl;

      await this.skipUnplayable(track);

      if (previous && this.PlaylistControl === previous) {
        this.stopCurrentTrack();
      }

      return "skipped";
    }

    this.stopCurrentTrack();

    const volume = this.currentVolume;

    const playlistControl = new PlaylistTrack(
      this.AudioContext,

      this.MediaStreamDestination,

      url,
    );

    this.PlaylistControl = playlistControl;

    this.startedAnnounced = false;

    try {
      const started = await playlistControl.play();

      // Entró OTRO `play()` durante el `await`: ya destruyó este control e
      // instaló el suyo. De acá para abajo todo es del control vivo, no de
      // éste, y emitir `Started` reportaba como sonando una pista que nadie
      // está escuchando. Tampoco se saltea: el reemplazo no es una pista rota.

      if (this.PlaylistControl !== playlistControl) return "skipped";

      // `false` con el <audio> ya cargado = el arranque lo interrumpió un
      // `pause()` (el del usuario, o el del disparador `playlist_pause`). La
      // pista NO está sonando, pero está cargada y en pausa: el próximo
      // `resume()` la pone a sonar, así que hay que cablearla igual. Sin el
      // `ended` la pista reanudada suena hasta el final y la rotación no
      // avanza nunca —aire muerto—, y sin el volumen heredado vuelve a sonar a
      // 1 aunque el usuario lo tenga en 0.2. `isPaused()` devuelve `undefined`
      // justo cuando no quedó <audio>: ahí no hay nada que cablear.

      const loaded = started || playlistControl.isPaused() !== undefined;

      if (!loaded) return "skipped";

      // La pista es reproducible —arrancó o quedó cargada en pausa—, así que
      // la racha de irreproducibles se corta acá.

      this.unplayableStreak = 0;

      this.setVolume(volume);

      playlistControl.onTrackEnded(() => {
        PlaylistEventsManager.emit(PlaylistEvents.TrackEnded, {
          trackId: track.id,
        });

        if (!this.isLoopEnabled) {
          const playlistSize = this.Data.size;

          if (this.currentTrackIndex + 1 >= playlistSize) {
            PlaylistEventsManager.emit(PlaylistEvents.PlaylistEmpty);

            PlaylistEventsManager.emit(PlaylistEvents.Ended);
          }

          return;
        }

        this.nextTrack();
      });

      // Un arranque interrumpido no se anuncia como reproducción —el
      // transporte de la UI sale de este booleano— y tampoco se saltea:
      // saltear una pausa arrancaría la pista SIGUIENTE justo después de que
      // el usuario paró la música.

      if (!started) return "loaded";

      PlaylistEventsManager.emit(PlaylistEvents.Started);

      this.startedAnnounced = true;

      return "started";
    } catch (error) {
      console.error("Error playing video:", error);

      // La MISMA guarda de identidad que arriba, ahora en el camino de error.
      // Un `play()` concurrente pudo haber reemplazado este control y el viejo
      // rechazar después (al decodificar, por ejemplo). Saltear ahí movía el
      // índice e interrumpía al control NUEVO: el usuario elegía una pista y a
      // los segundos le arrancaba otra sola, por culpa de la que ya descartó.

      if (this.PlaylistControl !== playlistControl) return "skipped";

      await this.skipUnplayable(track, error);

      return "skipped";
    }
  }

  /**
   * Detiene la pista que está sonando y se queda con su volumen. Lo llama el
   * camino que instala una pista nueva —ahí el <audio> viejo tiene reemplazo—
   * y, después de saltear, el que descubre que NO se instaló ninguna: sin eso
   * el <audio> anterior se queda sin nadie que lo pare, con la UI detenida y
   * la emisión al aire con música.
   */
  private stopCurrentTrack() {
    const control = this.PlaylistControl;

    if (!control) return;

    const previous = control.getVolume();

    // `null` = la pista anterior nunca creó su <audio>. Distinto de 0, que es
    // un volumen válido: tomar ese 0 arrancaría la siguiente en silencio.

    if (previous !== null && previous !== undefined) {
      this.currentVolume = previous;
    }

    control.destroy();

    // Soltar la referencia: un control destruido que sigue colgado acá retiene
    // su <audio> y el blob que haya quedado adentro (hasta el tope de la
    // bajada remota) en el MISMO renderer que compone la emisión. El camino
    // que cierra la rotación con `PlaylistEmpty`/`Ended` no instala ninguno
    // nuevo, así que sin esto la referencia vivía hasta la próxima pista.

    if (this.PlaylistControl === control) this.PlaylistControl = null;

    this.startedAnnounced = false;
  }

  /**
   * Una pista que no arranca no puede dejar la rotación clavada: se avisa y se
   * pasa a la siguiente. NO se emite `TrackEnded` — ese evento dispara el
   * trigger `track_ended` del usuario y la pista nunca sonó. Cuando la vuelta
   * completa no encuentra ninguna reproducible se cierra con el mismo par de
   * eventos que el final de lista, para no dejar esperando a la UI.
   */
  private async skipUnplayable(track: IPlaylistDB, error?: unknown) {
    console.warn(
      "[Playlist] Pista no reproducible; se salta:",

      track.name,

      error ?? "",
    );

    this.unplayableStreak += 1;

    const playlistSize = this.Data.size;

    const isLastTrack = this.currentTrackIndex + 1 >= playlistSize;

    if (
      this.unplayableStreak >= playlistSize ||
      (!this.isLoopEnabled && isLastTrack)
    ) {
      this.unplayableStreak = 0;

      PlaylistEventsManager.emit(PlaylistEvents.PlaylistEmpty);

      PlaylistEventsManager.emit(PlaylistEvents.Ended);

      return;
    }

    await this.nextTrack();
  }

  async nextTrack() {
    const playlistSize = this.Data.size;

    if (playlistSize === 0) return;

    let nextTrackIndex = this.currentTrackIndex + 1;

    if (nextTrackIndex >= playlistSize) {
      nextTrackIndex = 0;
    }

    const playlistArr = Array.from(this.Data.values());

    const newTrack = playlistArr[nextTrackIndex];

    let outcome: PlayOutcome = "skipped";

    try {
      outcome = await this.playTrack(nextTrackIndex);
    } catch (error) {
      console.error("Error playing video:", error);
    }

    // Solo si ESTA pista quedó de actual: si hubo que saltear, el `ChangeTrack`
    // de la que sí suena lo emitió el `nextTrack()` de adentro del salteo, y
    // cuando la vuelta entera se agotó no hay ninguna que anunciar.
    //
    // La señal es "cargada", no "sonando": una pista que el usuario pausó
    // mientras se bajaba ES la actual aunque todavía no suene. Con `started`
    // la pantalla se quedaba mostrando la pista ANTERIOR —nombre, fila
    // resaltada y duración de la barra— y al apretar Play sonaba otra.

    if (outcome !== "skipped" && newTrack) {
      PlaylistEventsManager.emit(PlaylistEvents.ChangeTrack, newTrack);
    }
  }

  async prevTrack() {
    const playlistSize = this.Data.size;

    if (playlistSize === 0) return;

    let prevTrackIndex = this.currentTrackIndex - 1;

    if (prevTrackIndex < 0) {
      prevTrackIndex = playlistSize - 1;
    }

    const playlistArr = Array.from(this.Data.values());

    const prevTrack = playlistArr[prevTrackIndex];

    let outcome: PlayOutcome = "skipped";

    try {
      outcome = await this.playTrack(prevTrackIndex);
    } catch (error) {
      console.error("Error playing video:", error);
    }

    if (outcome !== "skipped" && prevTrack) {
      PlaylistEventsManager.emit(PlaylistEvents.ChangeTrack, prevTrack);
    }
  }

  /**
   * `true` solo si volvió a sonar. Sin pista cargada no hay nada que reanudar,
   * y el transporte de la UI se deriva de este booleano: darlo por bueno
   * siempre dejaba el botón en "pausar" con silencio.
   */
  async resume(): Promise<boolean> {
    const control = this.PlaylistControl;

    if (!control) return false;

    let resumed = false;

    try {
      resumed = await control.resumeTrack();
    } catch (error) {
      // Una pista que quedó cargada y en pausa nunca pasó por el `play()` del
      // <audio>, así que su primer reanudar es también la PRIMERA prueba de que
      // los bytes se decodifican. Un rechazo de codec acá era aire muerto: la
      // rotación se quedaba estacionada en una pista que no suena ni dispara
      // `ended`, y cada Play posterior se tragaba el mismo rechazo.

      if (this.PlaylistControl !== control) return false;

      const track = Array.from(this.Data.values())[this.currentTrackIndex];

      this.PlaylistControl = null;

      this.startedAnnounced = false;

      if (track) await this.skipUnplayable(track, error);

      return false;
    }

    // La MISMA guarda de identidad que el camino de error, ahora en el camino
    // feliz. El `await` de arriba puede durar TODA una bajada —el reanudar de
    // una pista que todavía se está bajando espera al arranque en vuelo—, y en
    // ese rato un `play()` del usuario o un `nextTrack()` de la rotación
    // instalan OTRO control y destruyen éste. Sin la guarda, ese reanudar
    // tardío anunciaba el arranque por una pista que ya no está —la que sí
    // está puede seguir cargando—, le devolvía "sonando" a la UI y encima
    // marcaba `startedAnnounced` del control NUEVO, que entonces nunca
    // anunciaba el suyo.

    if (this.PlaylistControl !== control) return false;

    // La pista que el usuario pausó MIENTRAS se bajaba nunca emitió `Started`
    // —no llegó a sonar—, así que el disparador `playlist_started` se saltaba
    // esa pista entera. Se emite acá, una sola vez por control.

    if (resumed && !this.startedAnnounced) {
      this.startedAnnounced = true;

      PlaylistEventsManager.emit(PlaylistEvents.Started);
    }

    return resumed;
  }

  async update() {
    const db = await openDatabase();

    const playlist = await db.playlist.toArray();

    if (!playlist) return;

    await this.set(playlist);
  }

  pause() {
    const result = this?.PlaylistControl?.pauseTrack();

    PlaylistEventsManager.emit(PlaylistEvents.Stopped);

    return result;
  }

  isPaused() {
    return this?.PlaylistControl?.isPaused();
  }

  seek(time: number) {
    // Con guarda como todo el resto: la barra de progreso se puede arrastrar
    // sin haber tocado Play nunca —la pantalla le pone la duración PERSISTIDA
    // de la primera fila apenas abre—, y ahí no hay control instalado. Sin el
    // `?.` eso era un TypeError por cada mousemove del arrastre.

    this.PlaylistControl?.seekTo(time);
  }

  async setVolume(volume: number) {
    this.currentVolume = volume;

    if (this.PlaylistControl) this.PlaylistControl.updateVolume(volume);
  }

  setLoopEnabled(enabled: boolean) {
    this.isLoopEnabled = enabled;
  }

  isLoopModeEnabled(): boolean {
    return this.isLoopEnabled;
  }

  getAnalyser(): AnalyserNode | null {
    return this.PlaylistControl?.getAnalyser() ?? null;
  }

  destroy() {
    if (!this.PlaylistControl) return;

    this.PlaylistControl.destroy();

    this.PlaylistControl = null;

    this.startedAnnounced = false;

    this.Data.clear();
  }
}

export default Playlist;
